import { get as GET, delete as DELETE, post as POST, put as PUT } from 'axios'
import { editUsers } from '@/service/users'

// initial state
function initialState() {
  return {
    users: undefined,
    selectedUser: undefined,
    limit: 25,
    metadata: { total: 0 },
  }
}

function transformFilterFields(fields) {
  const assignedFields = Object.assign({ limit: initialState().limit }, fields)
  return Object.keys(assignedFields)
    .filter(key => !!assignedFields[key])
    .map(key => `${key}=${assignedFields[key]}`)
    .join('&')
}

const state = initialState()

// getters
const getters = {
  limit: state => state.limit,
  users: state => state.users,
  selectedUser: state => state.selectedUser,
  usersSize: state => state.metadata.total,
}

const actions = {
  anonymize(_, id) {
    return PUT(`/users/anonymize`, { id })
  },
  fetch({ commit }, f) {
    commit('clearUsers')
    const fields = transformFilterFields(f)
    const url = `/users?${fields}`

    return GET(url).then(resources => {
      commit('setUsers', resources.data.users)
      commit('setMetadata', resources.data.metadata)
    })
  },
  fetchMore({ commit, state }, { status, offset, role, name }) {
    let url = `/users?status=${status}&limit=${state.limit}&offset=${offset}`
    if (role && role !== 'null') url += `&role=${role}`
    if (name && name !== 'null') url += `&name=${name}`

    return GET(url)
      .then(resources => {
        commit('pushUsers', resources.data.users)
      })
      .catch(() => {})
  },
  resendInvite(_, { email, jwt }) {
    return POST(`/users/resend/invite`, { email: email, jwt: jwt })
  },
  cancelInvite({ commit }, id) {
    return DELETE(`/users/invite/${id}`).then(() => {
      commit('deleteUserFromList', id)
    })
  },
  update({ commit }, { profile, role, id, birthDate, attributes }) {
    return PUT(`/users/${id}`, {
      profile,
      role,
      birthDate,
      attributes,
    }).then(() => {
      commit('selectUser', undefined)
    })
  },
  updateMe(_, { profile }) {
    return PUT(`/users/me`, { profile })
  },
  changePassword(_, { currentPassword, newPassword }) {
    return PUT(`/users/password`, { currentPassword, newPassword })
  },
  changeSecretQuestion(_, { secretQuestion, secretQuestionAnswer }) {
    return PUT(`/users/secret-question`, {
      secretQuestion,
      secretQuestionAnswer,
    })
  },
  editUserLanguage(_, language) {
    return editUsers({ preferences: { language: language } })
  },
  delete({ commit }, id) {
    return DELETE(`/users/${id}`).then(() => {
      commit('updateUserStatus', { id, status: 'deactivated' })
    })
  },
  reactivate({ commit }, id) {
    return PUT(`/users/reactivate`, { id }).then(() => {
      commit('updateUserStatus', { id, status: 'confirmed' })
    })
  },
  inactivate({ commit }, id) {
    return PUT(`/users/inactivate`, { id }).then(() => {
      commit('updateInactiveUser', { id, inactive: true })
    })
  },
  disconfirm({ commit }, id) {
    return PUT(`/users/disconfirm`, { id }).then(() => {
      commit('updateUserStatus', { id, status: 'unconfirmed' })
    })
  },
  undoInactivate({ commit }, id) {
    return PUT(`/users/undo-inactivate`, { id }).then(() => {
      commit('updateInactiveUser', { id, inactive: false })
    })
  },
}

// mutations
const mutations = {
  setUsers(state, users) {
    state.users = users
  },
  setMetadata(state, metadata) {
    state.metadata = metadata
  },
  pushUsers(state, users) {
    state.users = state.users.concat(users)
  },
  clearUsers(state) {
    state.users = undefined
  },
  selectUser(state, selectedUser) {
    state.selectedUser = selectedUser
  },
  deleteUserFromList(state, id) {
    state.users = state.users.filter(u => u.id !== id)
  },
  updateUserStatus(state, { id, status }) {
    state.users = state.users.map(u => {
      if (u.id !== id) return u
      u.status = status
      return u
    })
  },
  updateInactiveUser(state, { id, inactive }) {
    state.users = state.users.map(u => {
      if (u.id !== id) return u
      u.inactive = inactive
      return u
    })
  },
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}
